import {useState} from 'react';

import {useThemeColors} from '@nfq/colors';

import type {Hash, TabComponent} from '../../../utils/childTypes';

/**
 * Custom hook that manages tab navigation, tracking the active tab and direction of the tab change.
 * It provides functionality to handle tab switching and update the current active tab.
 *
 * @param initialTab           The hash representing the initial active tab.
 * @param children             An array of TabComponent elements representing the content of each tab.
 * @param isPremountingAllTabs A boolean flag indicating whether all tabs should be pre-mounted in the DOM, even if they are not currently active.
 * @returns An object containing the current tab, the direction of the tab change, and a function to handle tab switching.
 *
 * @example
 * ```tsx
 * const { direction, handleTab, tab } = useTabs('tab1');
 *
 * return (
 *   <Tabs activeTab={tab}>
 *     <Tab onClick={() => handleTab('tab2')}>Tab 2</Tab>
 *   </Tabs>
 * );
 * ```
 */
export const useTabs = (initialTab: Hash, children: TabComponent[], isPremountingAllTabs = false) => {
    const [direction, setDirection] = useState<'backwards' | 'forwards'>('forwards');
    const [tab, setTab] = useState(initialTab);
    const activeChild = children.find(child => child.key === tab);
    const inactiveChilds = isPremountingAllTabs ? children.filter(child => child.key !== tab) : [];
    const colors = useThemeColors();

    /**
     * Handles the action of changing tabs in a tabbed interface.
     * This function updates the URL hash to reflect the current active tab based on the provided hash value.
     * It is intended to be used as an event handler in a tab navigation system.
     *
     * @param hash The hash identifier of the tab to be activated.
     *
     * @example
     * ```tsx
     * const handleTabChange = handleTab;
     * <Tab onClick={() => handleTabChange('tabHash')}>
     *     Tab Label
     * </Tab>
     * ```
     */
    const handleTab = (hash: Hash) => {
        setDirection(hash > tab ? 'forwards' : 'backwards');
        setTab(hash);
    };

    return {
        activeChild,
        colors,
        direction,
        handleTab,
        inactiveChilds,
        tab
    };
};